/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    ICheckoutViewProps,
    ILineItem,
    ILineItemDeliveryGroup,
    ILineItems,
    IOrderSummary,
    IPickUpAtStore
} from '@msdyn365-commerce-modules/checkout/src/modules/checkout/./checkout';
import { IEmailDelivery } from '@msdyn365-commerce-modules/checkout/src/modules/checkout/./components';
import { IInvoicePaymentSummary } from '@msdyn365-commerce-modules/checkout/src/modules/checkout/./components/get-invoice-payment-summary';
import { IChargeLinesBreakdownData, ICheckoutProps as ICheckoutExtensionProps } from '../definition-extensions/checkout.ext.props.autogenerated';
import { AttributeBooleanValue, Cart } from '@msdyn365-commerce/retail-proxy';

export const PickUpAtStoreComponent: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export interface IOrderSummaryExtended extends IOrderSummary {
    otherChargesLines: React.ReactNode[];
    displayDefaultShippingLine: boolean | undefined;
}


export const EmailDeliveryComponent: React.FC<IEmailDelivery> = ({ EmailDelivery, label }) => <Node {...EmailDelivery}>{label}</Node>;

export const LineItemComponent: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore, emailDelivery }) => (
    <Node {...LineItem}>
        {item}
        {pickUpAtStore && <PickUpAtStoreComponent {...pickUpAtStore} />}
        {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
    </Node>
);

export const LineItemGroupComponent: React.FC<ILineItemDeliveryGroup> = ({ LineItemDeliveryGroup, LineItemList, heading, lineItems }) => (
    <Node {...LineItemDeliveryGroup}>
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponent key={lineItem.LineId} {...lineItem} />
            ))}
        </Node>
    </Node>
);

export const LineItemGroupComponentWithMultiplePickUp: React.FC<ILineItemDeliveryGroup> = ({
    LineItemDeliveryGroup,
    LineItemList,
    heading,
    lineItems,
    lineItemWraper,
    lineItemWraperIcon
}) => (
    <Node {...LineItemDeliveryGroup}>
        {lineItemWraperIcon}
        {lineItemWraper}
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponentWithMultiplePickUp key={lineItem.LineId} {...lineItem} />
            ))}
        </Node>
    </Node>
);

export const LineItemComponentWithMultiplePickUp: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore, emailDelivery }) => (
    <Node {...LineItem}>
        {item}
        {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
    </Node>
);

export const PickUpAtStoreComponentWithMultiplePickUp: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const LineItemsComponent: React.FC<ILineItems> = ({
    LineItems,
    Header,
    heading,
    editLink,
    itemsForPickup,
    itemsForShip,
    itemsForEmail,
    itemsGroupWithMulitplePickupMode
}) => (
    <Node {...LineItems}>
        <Node {...Header}>
            {heading}
            {editLink}
        </Node>
        {itemsGroupWithMulitplePickupMode === undefined && itemsForPickup && <LineItemGroupComponent {...itemsForPickup} />}
        {itemsGroupWithMulitplePickupMode === undefined && itemsForEmail && <LineItemGroupComponent {...itemsForEmail} />}
        {itemsGroupWithMulitplePickupMode === undefined && itemsForShip && <LineItemGroupComponent {...itemsForShip} />}
        {itemsGroupWithMulitplePickupMode !== undefined
            ? itemsGroupWithMulitplePickupMode.map((item, index) => {
                return <LineItemGroupComponentWithMultiplePickUp {...item} key={index} />;
            })
            : null}
    </Node>
);

const OrderSummaryComponent: React.FC<IOrderSummaryExtended> = ({ heading, lines, otherChargesLines, displayDefaultShippingLine }) => (
    <div className='msc-order-summary-wrapper'>
        {heading}
        <div className='msc-order-summary__items'>
            {lines && (
                <>
                    {lines.subtotal}
                    {displayDefaultShippingLine && lines.shipping}
                    {/* {lines.otherCharge} */}
                    {otherChargesLines && otherChargesLines}
                    {lines.tax}
                    {lines.totalDiscounts}
                    {lines.loyalty}
                    {lines.giftCard}
                    {lines.orderTotal}
                </>
            )}
        </div>
    </div>
);

const PaymentSummaryComponent: React.FC<IInvoicePaymentSummary> = ({ heading, lines }) => (
    <div className='msc-invoice-summary-wrapper'>
        {heading}
        <div className='msc-invoice-summary__items'>
            {lines && (
                <>
                    {lines.invoices}
                    {lines.giftCard}
                    {lines.loyalty}
                    {lines.orderTotal}
                </>
            )}
        </div>
    </div>
);


const otherChargesLines = (cart: Cart, chargeCodes: IChargeLinesBreakdownData[] | undefined, currencyFormatter: (price: string | number, currencyCode?: string | undefined) => string, displayDefaultShippingLine: boolean | undefined): React.ReactNode[] => {

    let lines = cart.ChargeLines;
    let linesNode: React.ReactNode[] = [];
    let otherChargeTotal = cart.OtherChargeAmount || 0;
    chargeCodes?.map(c => {
        const node = lines?.filter(
            line => line.ChargeCode === c.chargeCode).map(
                r => {
                    otherChargeTotal -= r.CalculatedAmount || 0;
                    return _renderLineChargeNode(c.chargeLineLabel || '', currencyFormatter(r.CalculatedAmount?.toString() ?? ""));
                }
            );
        linesNode.push(node);
    })
    // if the remaining amount is over 1 cent, display the other Charges line. This avoids potential fraction of a penny being displayed as zero.
    if (otherChargeTotal >= 0.01) {
        const otherChargesNode = _renderLineChargeNode('Other Charges', currencyFormatter(otherChargeTotal.toString() ?? ""));
        linesNode.push(otherChargesNode);
    }
    // if no charge exists, display shipping to be calculated text 
    // if (cart.ChargeAmount === 0 && !displayDefaultShippingLine) {
    //     const shippingToBeDetermined = _renderLineChargeNode('Shipping', 'To be calculated');
    //     linesNode.push(shippingToBeDetermined);
    // }
    return linesNode;
}

const _renderLineChargeNode = (chargeName: string, chargeAmount: string): React.ReactNode => {
    return (
        <p className='msc-order-summary__line-other-charges'>
            <span className='msc-order-charge-line__label'>{chargeName}</span> <span className='msc-price msc-order-summary__value'>{chargeAmount}</span>
        </p>
    );
};


const _renderShipOnCheckout = (
    shipCompleteOnCheckout: boolean,
    handleShipCompleteOnCheckoutChange: (e: React.MouseEvent<HTMLInputElement, MouseEvent>) => void,
    shipCompleteOnCheckoutLabel: string
): React.ReactNode => (
    <div className='ms-checkout__ship-on-checkout'>
        <label className='input-group-prepend print-view-toggle__label'>
            <span className={'print-view-toggle__heading'}>{shipCompleteOnCheckoutLabel}</span>
            <input
                value={shipCompleteOnCheckout ? 1 : 0}
                onClick={handleShipCompleteOnCheckoutChange}
                type='checkbox'
                className='print-view-toggle__checkbox'
            />
            <div className={`print-view-toggle__toggle print-view-toggle__toggle--${shipCompleteOnCheckout ? 'checked' : 'unchecked'}`} />
        </label>
    </div>
);

const _renderOrderComment = (commentHeading: string, commentString: string): React.ReactNode => {
    const emptyStringClassName = commentString == '';
    return (
        <div className={`order-level-comment-wrapper ${emptyStringClassName ? "order-level-comment-wrapper--empty-string" : ""}`}>
            <h1 className="order-level-comment-wrapper__heading">
                {commentHeading}
            </h1>
            <p className="order-level-comment-wrapper__text">
                {commentString}
            </p>
        </div>
    );
}

const CheckoutView: React.FC<ICheckoutViewProps & ICheckoutExtensionProps<{}>> = props => {


    const attributeArr = props.data.checkout.result?.checkoutCart.cart.AttributeValues || [];

    const getShipCompleteIndex = (): number => {
        return props.data.checkout.result?.checkoutCart.cart.AttributeValues?.findIndex(attribute => attribute.Name === 'ShipComplete') || -1;
    }
    // Get the text in the order level comment, if it doesn't exist, assign an empty string
    const getCommentText = (): string => {
        return props.data.checkout.result?.checkoutCart.cart.Comment || '';
    }
    // Get the comment heading text from the resources
    const getCommentHeadingText = (): string => {
        return props.resources.commentHeadingText;
    }
    const getShipCompleteValue = (): boolean => {
        const shipCompleteIndex = getShipCompleteIndex();
        if (shipCompleteIndex === -1) {
            return false;
        } else {
            const attr = attributeArr[shipCompleteIndex] as AttributeBooleanValue;
            return !!attr?.Value;
        }
    };
    const handleShipCompleteOnCheckoutChange = () => {
        const shipComplete = {
            '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeBooleanValue',
            Name: 'ShipComplete',
            Value: !getShipCompleteValue()
        };
        const idx = getShipCompleteIndex();
        idx === -1 ? attributeArr.push(shipComplete) : attributeArr.splice(idx, 1, shipComplete);
        const cart: Cart = Object.assign({}, props.data.checkout.result?.checkoutCart.cart);
        if (cart) {
            cart.AttributeValues = attributeArr;
            saveShipCompleteToCart(cart);
        }
    };
    const saveShipCompleteToCart = async (cart: Cart) => {
        if (cart) {
            const checkoutState = props.data.checkout.result;
            if (!checkoutState) {
                return;
            }

            await checkoutState.checkoutCart.updateCart({ newCartObject: cart }).then(() => {
                setShipCompleteOnCheckout(getShipCompleteValue());
            });
        }
    };
    const [shipCompleteOnCheckout, setShipCompleteOnCheckout] = React.useState<boolean>(getShipCompleteValue());
    const shipCompleteOnCheckoutToggle = _renderShipOnCheckout(
        shipCompleteOnCheckout,
        handleShipCompleteOnCheckoutChange,
        props.resources.shipCompleteOnCheckoutLabel
    );
    const {
        canShow,
        checkoutProps,
        headerProps,
        hasSalesOrder,
        hasInvoiceLine,
        bodyProps,
        mainProps,
        mainControlProps,
        sideProps,
        sideControlFirstProps,
        sideControlSecondProps,
        termsAndConditionsProps,
        orderConfirmation,
        loading,
        alert,
        title,
        guidedForm,
        orderSummary,
        invoicePaymentSummary,
        lineItems,
        placeOrderButton,
        termsAndConditions,
        keepShoppingButton,
        config: { chargeLinesBreakdown, displayDefaultShippingLine },
        context: { cultureFormatter }
    } = props;
    const cart: Cart = Object.assign({}, props.data.checkout.result?.checkoutCart.cart);

    let chargelines = otherChargesLines(cart, chargeLinesBreakdown, cultureFormatter.formatCurrency, displayDefaultShippingLine);
    const orderSummaryExtended: IOrderSummaryExtended = { ...orderSummary!, otherChargesLines: chargelines, displayDefaultShippingLine: displayDefaultShippingLine };

    return (
        <Module {...checkoutProps}>
            {!hasSalesOrder && <Node {...headerProps}>{title}</Node>}
            {!hasSalesOrder && (
                <Node {...bodyProps}>
                    {loading}
                    {alert}
                    {canShow && (
                        <>
                            <Node {...mainProps}>
                                {guidedForm}
                                <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                {props.config.shipCompleteOnCheckout && shipCompleteOnCheckoutToggle}
                                <Node {...mainControlProps}>
                                    {placeOrderButton}
                                    {keepShoppingButton}
                                </Node>
                            </Node>
                            <Node {...sideProps}>
                                {!hasInvoiceLine
                                    ? orderSummary && <OrderSummaryComponent {...orderSummaryExtended} />
                                    : invoicePaymentSummary && <PaymentSummaryComponent {...invoicePaymentSummary} />}
                                <Node {...sideControlFirstProps}>
                                    <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                    {placeOrderButton}
                                    {keepShoppingButton}
                                </Node>
                                {lineItems && <LineItemsComponent {...lineItems} />}
                                {_renderOrderComment(getCommentHeadingText(), getCommentText())}
                                <Node {...sideControlSecondProps}>
                                    <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                    {placeOrderButton}
                                    {keepShoppingButton}
                                </Node>
                            </Node>
                        </>
                    )}
                </Node>
            )}
            {hasSalesOrder && orderConfirmation}
        </Module>
    );
};

export default CheckoutView;
